import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  Skeleton,
  CardActions,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/helpers';
import { isActivityFull, getSpotsLabel } from '../../utils/activityUtils';
import { canCancelRegistration, cancellationCutoffMessage } from '../../utils/cancellationPolicy';
import ModifyParticipantsDialog from './ModifyParticipantsDialog';

const ActivityCard = ({ activity, showCancelButton, onCancelRegistration, onModifyParticipants, numberOfParticipants }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [openModifyDialog, setOpenModifyDialog] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isPast = new Date(activity.date) < new Date();
  const canCancel = canCancelRegistration(activity);
  const isFull = isActivityFull(activity);

  const handleModifyParticipants = async (newNumberOfParticipants, useSubscription) => {
    try {
      setError('');
      await onModifyParticipants(activity._id, newNumberOfParticipants, useSubscription);
      setOpenModifyDialog(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error modifying number of participants');
    }
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          cursor: 'pointer',
          '&:hover': {
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease-in-out',
          },
        }}
        onClick={() => navigate(`/activities/${activity.slug}`)}
      >
        {activity.images?.[0] && (
          <>
            {!imageLoaded && (
              <Skeleton 
                variant="rectangular" 
                height={200}
                animation="wave"
              />
            )}
            <CardMedia
              component="img"
              height="200"
              image={getImageUrl(activity.images[0].url)}
              alt={activity.images[0].alt || activity.title}
              sx={{ display: imageLoaded ? 'block' : 'none' }}
              onLoad={() => setImageLoaded(true)}
            />
          </>
        )}
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom variant="h5" component="h2">
            {activity.title}
          </Typography>
          
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label={activity.category}
              color="primary"
              size="small"
            />
            <Chip
              label={isPast ? t('past') : t('upcoming')}
              color={isPast ? 'default' : 'success'}
              size="small"
            />
            <Chip
              label={getSpotsLabel(activity, t)}
              color={isFull ? 'error' : 'info'}
              size="small"
            />
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {activity.shortDescription}
          </Typography>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {format(new Date(activity.date), 'EEEE PPP', { locale: fr })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {format(new Date(activity.date), 'p', { locale: fr })} ({activity.duration} {t('minutes')})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2" noWrap>
                {activity.location.name}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/activities/${activity.slug}`);
              }}
              fullWidth
            >
              {t('viewDetails')}
            </Button>
            {showCancelButton && !isPast && canCancel && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelRegistration(activity);
                }}
                startIcon={<CancelIcon />}
                fullWidth
              >
                {t('cancel')}
              </Button>
            )}
          </Stack>

          {showCancelButton && !isPast && !canCancel && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {cancellationCutoffMessage(activity)}
            </Typography>
          )}

          {!isPast && showCancelButton && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenModifyDialog(true);
                }}
                fullWidth
                startIcon={<GroupIcon />}
              >
                {t('modifyParticipants')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <ModifyParticipantsDialog
        open={openModifyDialog}
        onClose={() => setOpenModifyDialog(false)}
        onModify={handleModifyParticipants}
        currentParticipants={numberOfParticipants}
        maxParticipants={activity.maxParticipants}
        currentRegistrations={activity.currentParticipants}
        category={activity.category}
        error={error}
      />
    </>
  );
};

export default ActivityCard; 